import { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, List, Typography } from '@mui/material';

import { AuthStoreContext } from '../../Root';
import DeviceStore from '../../../stores/dev-store';
import { DeviceInfo, DeviceType } from '../../../types/device';
import { SensorType } from '../../../types/sensor';
import DeviceView from './DeviceView';
import DeviceSkeleton from './DeviceSceleton';
import { Selection } from '../selection';

//=================================================================================================
function DeviceListSkeleton() {
    return (
        <>
            {[0, 1, 2, 3].map((i) => (
                <DeviceSkeleton key={i} />
            ))}
        </>
    );
}

function DeviceLoadError() {
    return (
        <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="error">
                Ошибка загрузки устройств
            </Typography>
        </Box>
    );
}

function DeviceEmpty() {
    return (
        <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
                Устройства не найдены
            </Typography>
        </Box>
    );
}

//=================================================================================================
function DeviceItem({
    device,
    selection,
    onSelect,
}: {
    device: DeviceInfo;
    selection?: Selection;
    onSelect: (sel: Selection) => void;
}) {
    const info = device.info ?? {};
    return (
        <DeviceView
            deviceId={device.device_id}
            deviceType={(info.deviceType as DeviceType) ?? DeviceType.Device_Unknown}
            sensorType={(info.sensorType as SensorType) ?? SensorType.UnknownSensorType}
            sensorId={info.sensorId ?? 0}
            lastTransaction={info.lastTransaction ?? new Date().toISOString()}
            logConfig={info.logConfig}
            nodeId={device.node_id}
            battery={info.battery}
            rssi={info.rssi}
            sensorState={info.sensorState}
            selected={selection?.kind === 'device' && selection.deviceId === device.device_id}
            onClick={(deviceId) => onSelect({ kind: 'device', deviceId: deviceId, nodeId: device.node_id })}
        />
    );
}

//=================================================================================================
interface DeviceLayoutProps {
    selection?: Selection;
    onSelect?: (sel: Selection) => void;
}

export const DeviceLayout = observer(({ selection, onSelect }: DeviceLayoutProps) => {
    const { authStore } = useContext(AuthStoreContext);
    const user = authStore.user;

    const [store] = useState(() => new DeviceStore(user?.node_id ?? 0));
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!user) return;
        store.load().catch(() => setError(true));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!user) return null;

    const handleSelect = (sel: Selection) => onSelect?.(sel);

    let content: React.ReactNode;
    if (store.loading) {
        content = <DeviceListSkeleton />;
    } else if (error) {
        content = <DeviceLoadError />;
    } else if (store.items.length === 0) {
        content = <DeviceEmpty />;
    } else {
        content = store.items.map((d) => (
            <DeviceItem key={d.device_id} device={d} selection={selection} onSelect={handleSelect} />
        ));
    }

    return (
        <List dense disablePadding>
            {content}
        </List>
    );
});

export default DeviceLayout;
